import { XUIPanelSection } from '@xero/xui/react/panel';
import { XUIPickitem } from '@xero/xui/react/picklist';
import { ChangeEvent, Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DropDownSelect } from '../../../../components/DropDownSelect';
import { InfoPopover } from '../../../../components/InfoPopover';
import Link from '../../../../components/Link';
import { createButtonContent, formatPickListItem } from '../../../../helpers/accountDropdown';
import { AccountSettingType, IAccount } from '../../../../models';
import { IShopifySettingsPutRequest, IShopifySettingsResponse } from '../../../../models/shopify';
import { settingsSelectors } from '../../../../redux/slices/SettingsSlice';

interface IShopifyRevenueTabProps {
    integrationLang: Record<string, any>;
    onSetSalesAccount: (salesAccountId: string) => void;
    onSetDiscountsAccount: (discountAccountId: string) => void;
    onSetShippingsAccount: (shippingAccountId: string) => void;
    onSetRefundsAccount: (refundAccountId: string) => void;
    onValid: (isValid: boolean) => void;
}
export const RevenueSetupTab: FC<IShopifyRevenueTabProps> = ({
    integrationLang,
    onSetSalesAccount,
    onSetDiscountsAccount,
    onSetShippingsAccount,
    onSetRefundsAccount,
    onValid,
}) => {
    const receivedSettings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsPutRequest;

    const salesAccountSettings = receivedSettings?.chartOfAccountsSettings.find(
        (f) => f.accountSettingType === AccountSettingType.Sales
    );
    const salesAccountEligible =
        receivedSettings?.chartOfAccounts.filter((f) => salesAccountSettings?.eligibleAccounts.includes(f.accountId)) ??
        [];
    const refundsAccountSettings = receivedSettings?.chartOfAccountsSettings.find(
        (f) => f.accountSettingType === AccountSettingType.Refunds
    );
    const refundsAccountEligible =
        receivedSettings?.chartOfAccounts.filter((f) =>
            refundsAccountSettings?.eligibleAccounts.includes(f.accountId)
        ) ?? [];
    const shippingAccountSettings = receivedSettings?.chartOfAccountsSettings.find(
        (f) => f.accountSettingType === AccountSettingType.Shipping
    );
    const shippingAccountEligible =
        receivedSettings?.chartOfAccounts.filter((f) =>
            shippingAccountSettings?.eligibleAccounts.includes(f.accountId)
        ) ?? [];
    const discountsAccountSettings = receivedSettings?.chartOfAccountsSettings.find(
        (f) => f.accountSettingType === AccountSettingType.Discounts
    );
    const discountsAccountEligible =
        receivedSettings?.chartOfAccounts.filter((f) =>
            discountsAccountSettings?.eligibleAccounts.includes(f.accountId)
        ) ?? [];

    const [salesAccountSearch, setSalesAccountSearch] = useState('');
    const clearInputSearch = (setState: Dispatch<SetStateAction<string>>) => setState('');
    const handleSalesAccountChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setSalesAccountSearch(event.target.value);
    const handleSalesAccountOnBlur = () => {
        clearInputSearch(setSalesAccountSearch);
    };
    const handleSalesAccountSelect = (account: IAccount) => () => {
        clearInputSearch(setSalesAccountSearch);
        onSetSalesAccount(account.accountId);
    };

    const [refundsAccountSearch, setRefundsAccountSearch] = useState('');
    const handleRefundsAccountChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setRefundsAccountSearch(event.target.value);
    const handleRefundsAccountOnBlur = () => {
        clearInputSearch(setRefundsAccountSearch);
    };
    const handleRefundsAccountSelect = (account: IAccount) => () => {
        clearInputSearch(setRefundsAccountSearch);
        onSetRefundsAccount(account.accountId);
    };

    const [shippingsAccountSearch, setShippingsAccountSearch] = useState('');
    const handleShippingsAccountChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setShippingsAccountSearch(event.target.value);
    const handleShippingsAccountOnBlur = () => {
        clearInputSearch(setShippingsAccountSearch);
    };
    const handleShippingsAccountSelect = (account: IAccount) => () => {
        clearInputSearch(setShippingsAccountSearch);
        onSetShippingsAccount(account.accountId);
    };

    const [discountsAccountSearch, setDiscountsAccountSearch] = useState('');
    const handleDiscountsAccountChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setDiscountsAccountSearch(event.target.value);
    const handleDiscountsAccountOnBlur = () => {
        clearInputSearch(setDiscountsAccountSearch);
    };
    const handleDiscountsAccountSelect = (account: IAccount) => () => {
        clearInputSearch(setDiscountsAccountSearch);
        onSetDiscountsAccount(account.accountId);
    };

    const DropDownButton = createButtonContent(receivedSettings?.chartOfAccounts!);

    useEffect(() => onValid(true), [settings, onValid]);

    return (
        <>
            <XUIPanelSection className='shopify-panel--section' heading={integrationLang.SETUP_PAGE.REVENUE_TAB.TITLE}>
                <div className='content shopify-content xui-padding-bottom-3xlarge'>
                    <div className='shopify-panel--description xui-padding-top-xlarge'>
                        {integrationLang.SETUP_PAGE.REVENUE_TAB.BLURB}
                    </div>
                    <DropDownSelect
                        buttonContent={
                            <DropDownButton
                                accountId={settings.salesAccountId}
                                defaultAccount={salesAccountSettings!}
                                setAccount={onSetSalesAccount}
                            >
                                {integrationLang.SETUP_PAGE.REVENUE_TAB.SELECT_ACCOUNT}
                            </DropDownButton>
                        }
                        buttonLabel={<div>{integrationLang.SETUP_PAGE.REVENUE_TAB.SALES} </div>}
                        showSearchBox={true}
                        showFooter={false}
                        onBlur={handleSalesAccountOnBlur}
                        onInputChange={handleSalesAccountChange}
                        qaHook='sales-account-dropdown'
                    >
                        {[
                            ...(salesAccountSettings && salesAccountSettings.defaultToSuggestion
                                ? [salesAccountSettings.suggestedAccount]
                                : []),
                            ...salesAccountEligible,
                        ]
                            .filter(
                                (f) =>
                                    salesAccountSearch === '' ||
                                    `${f.code}-${f.name}`.toLowerCase().includes(salesAccountSearch.toLowerCase())
                            )
                            .map((revenueAccount, i) => (
                                <XUIPickitem
                                    key={i + 1}
                                    id={String(i + 1)}
                                    onClick={handleSalesAccountSelect(revenueAccount)}
                                    isSelected={settings.salesAccountId === revenueAccount.accountId}
                                >
                                    {formatPickListItem(revenueAccount)}
                                </XUIPickitem>
                            ))}
                    </DropDownSelect>
                    <DropDownSelect
                        buttonContent={
                            <DropDownButton
                                accountId={settings.discountsAccountId}
                                defaultAccount={discountsAccountSettings!}
                                setAccount={onSetDiscountsAccount}
                            >
                                {integrationLang.SETUP_PAGE.REVENUE_TAB.SELECT_ACCOUNT}
                            </DropDownButton>
                        }
                        buttonLabel={<div>{integrationLang.SETUP_PAGE.REVENUE_TAB.DISCOUNTS} </div>}
                        showSearchBox={true}
                        showFooter={false}
                        onBlur={handleDiscountsAccountOnBlur}
                        onInputChange={handleDiscountsAccountChange}
                        qaHook='discounts-account-dropdown'
                    >
                        {[
                            ...(discountsAccountSettings && discountsAccountSettings.defaultToSuggestion
                                ? [discountsAccountSettings.suggestedAccount]
                                : []),
                            ...discountsAccountEligible,
                        ]
                            .filter(
                                (f) =>
                                    discountsAccountSearch === '' ||
                                    `${f.code}-${f.name}`.toLowerCase().includes(discountsAccountSearch.toLowerCase())
                            )
                            .map((revenueAccount, i) => (
                                <XUIPickitem
                                    key={i + 1}
                                    id={String(i + 1)}
                                    onClick={handleDiscountsAccountSelect(revenueAccount)}
                                    isSelected={settings.discountsAccountId === revenueAccount.accountId}
                                >
                                    {formatPickListItem(revenueAccount)}
                                </XUIPickitem>
                            ))}
                    </DropDownSelect>
                    <DropDownSelect
                        buttonContent={
                            <DropDownButton
                                accountId={settings.shippingAccountId}
                                defaultAccount={shippingAccountSettings!}
                                setAccount={onSetShippingsAccount}
                            >
                                {integrationLang.SETUP_PAGE.REVENUE_TAB.SELECT_ACCOUNT}
                            </DropDownButton>
                        }
                        buttonLabel={
                            <div>
                                {integrationLang.SETUP_PAGE.REVENUE_TAB.SHIPPING}{' '}
                                <InfoPopover>{integrationLang.SETUP_PAGE.REVENUE_TAB.SHIPPING_HINT}</InfoPopover>
                            </div>
                        }
                        showSearchBox={true}
                        showFooter={false}
                        onBlur={handleShippingsAccountOnBlur}
                        onInputChange={handleShippingsAccountChange}
                        qaHook='shipping-account-dropdown'
                    >
                        {[
                            ...(shippingAccountSettings && shippingAccountSettings.defaultToSuggestion
                                ? [shippingAccountSettings.suggestedAccount]
                                : []),
                            ...shippingAccountEligible,
                        ]
                            .filter(
                                (f) =>
                                    shippingsAccountSearch === '' ||
                                    `${f.code}-${f.name}`.toLowerCase().includes(shippingsAccountSearch.toLowerCase())
                            )
                            .map((revenueAccount, i) => (
                                <XUIPickitem
                                    key={i + 1}
                                    id={String(i + 1)}
                                    onClick={handleShippingsAccountSelect(revenueAccount)}
                                    isSelected={settings.shippingAccountId === revenueAccount.accountId}
                                >
                                    {formatPickListItem(revenueAccount)}
                                </XUIPickitem>
                            ))}
                    </DropDownSelect>
                    <DropDownSelect
                        buttonContent={
                            <DropDownButton
                                accountId={settings.refundsAccountId}
                                defaultAccount={refundsAccountSettings!}
                                setAccount={onSetRefundsAccount}
                            >
                                {integrationLang.SETUP_PAGE.REVENUE_TAB.SELECT_ACCOUNT}
                            </DropDownButton>
                        }
                        buttonLabel={<div>{integrationLang.SETUP_PAGE.REVENUE_TAB.REFUNDS} </div>}
                        showSearchBox={true}
                        showFooter={false}
                        onBlur={handleRefundsAccountOnBlur}
                        onInputChange={handleRefundsAccountChange}
                        qaHook='refunds-account-dropdown'
                    >
                        {[
                            ...(refundsAccountSettings && refundsAccountSettings.defaultToSuggestion
                                ? [refundsAccountSettings.suggestedAccount]
                                : []),
                            ...refundsAccountEligible,
                        ]
                            .filter(
                                (f) =>
                                    refundsAccountSearch === '' ||
                                    `${f.code}-${f.name}`.toLowerCase().includes(refundsAccountSearch.toLowerCase())
                            )
                            .map((revenueAccount, i) => (
                                <XUIPickitem
                                    key={i + 1}
                                    id={String(i + 1)}
                                    onClick={handleRefundsAccountSelect(revenueAccount)}
                                    isSelected={settings.refundsAccountId === revenueAccount.accountId}
                                >
                                    {formatPickListItem(revenueAccount)}
                                </XUIPickitem>
                            ))}
                    </DropDownSelect>
                    <div className='xui-padding-top-xlarge xui-text-align-center xui-stepper-link-description'>
                        {integrationLang.SETUP_PAGE.REVENUE_TAB.COA_HELP_PREAMBLE}
                        <Link
                            displayLinkIcon={true}
                            href={integrationLang.SETUP_PAGE.REVENUE_TAB.COA_HELP_LINK}
                        >
                            {integrationLang.SETUP_PAGE.REVENUE_TAB.COA_HELP_TEXT}
                        </Link>
                    </div>
                </div>
            </XUIPanelSection>
        </>
    );
};
